import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import * as api from '../../services/api';
import { UserSelectors } from '../../reducers/user';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

// ─── Page shell ─────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 28px 32px;
  max-width: 1280px;
  margin: 0 auto;
  animation: ${fadeIn} 0.25s ease;
`;

const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div``;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: #0F2042;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #64748B;
  margin: 0;
`;

// ─── Upload ──────────────────────────────────────────────────────────────────

const UploadZone = styled.div<{ $dragging: boolean }>`
  border: 2px dashed ${(p) => (p.$dragging ? '#0EA5E9' : '#CBD5E1')};
  border-radius: 12px;
  background: ${(p) => (p.$dragging ? '#F0F9FF' : '#FAFBFC')};
  padding: 18px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  min-width: 280px;
  &:hover { border-color: #0EA5E9; background: #F0F9FF; }
`;

const UploadIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0F2042, #0369A1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
`;

const UploadText = styled.div`
  flex: 1;
`;

const UploadLabel = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #0F2042;
`;

const UploadHint = styled.div`
  font-size: 12px;
  color: #94A3B8;
  margin-top: 1px;
`;

const UploadStatus = styled.div<{ $ok?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$ok ? '#16a34a' : '#dc2626')};
  margin-top: 4px;
`;

// ─── Hero stat cards ─────────────────────────────────────────────────────────

const HeroRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 28px;
  @media (max-width: 900px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;

const HeroCard = styled.div<{ $gradient: string }>`
  background: ${(p) => p.$gradient};
  border-radius: 14px;
  padding: 20px 18px;
  color: #fff;
  position: relative;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: -20px; right: -20px;
    width: 80px; height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,0.07);
  }
`;

const HeroLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.75;
  margin-bottom: 8px;
`;

const HeroValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1;
`;

const HeroUnit = styled.span`
  font-size: 13px;
  font-weight: 500;
  opacity: 0.7;
  margin-left: 4px;
`;

// ─── Two-column content area ──────────────────────────────────────────────────

const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 18px;
  margin-bottom: 24px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

// ─── Sessions panel ──────────────────────────────────────────────────────────

const Panel = styled.div`
  background: #fff;
  border: 1.5px solid #E2EAF4;
  border-radius: 14px;
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #E2EAF4;
  font-size: 13px;
  font-weight: 700;
  color: #0F2042;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  background: #EFF6FF;
  color: #0369A1;
  border-radius: 20px;
  padding: 2px 8px;
`;

const SessionList = styled.div`
  overflow-y: auto;
  max-height: 320px;
`;

const SessionItem = styled.div<{ $active: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#EFF6FF' : 'transparent')};
  border-left: 3px solid ${(p) => (p.$active ? '#0369A1' : 'transparent')};
  transition: background 0.12s;
  &:hover { background: ${(p) => (p.$active ? '#EFF6FF' : '#F8FAFC')}; }
  &:not(:last-child) { border-bottom: 1px solid #F1F5F9; }
`;

const SessionDate = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0F2042;
`;

const SessionMeta = styled.div`
  font-size: 11px;
  color: #64748B;
  margin-top: 2px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SessionRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const SessionDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #CBD5E1;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  flex-shrink: 0;
  &:hover { color: #EF4444; background: #FEF2F2; }
`;

const Badge = styled.span<{ $color?: string }>`
  background: ${(p) => p.$color || '#F1F5F9'};
  color: ${(p) => (p.$color ? '#fff' : '#64748B')};
  border-radius: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// ─── Club breakdown ──────────────────────────────────────────────────────────

const ClubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const ClubTh = styled.th`
  padding: 11px 14px;
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #94A3B8;
  background: #F8FAFC;
  border-bottom: 1px solid #E2EAF4;
  white-space: nowrap;
  &:not(:first-child) { text-align: right; }
`;

const ClubTd = styled.td<{ $bold?: boolean; $color?: string }>`
  padding: 11px 14px;
  color: ${(p) => p.$color || (p.$bold ? '#0F2042' : '#374151')};
  font-weight: ${(p) => (p.$bold ? 700 : 400)};
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  &:not(:first-child) { text-align: right; }
`;

const ClubDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  margin-right: 8px;
  flex-shrink: 0;
`;

// ─── Shot log table ───────────────────────────────────────────────────────────

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0F2042;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 6px 10px;
  font-size: 12.5px;
  border: 1.5px solid #E2EAF4;
  border-radius: 8px;
  color: #374151;
  background: #fff;
  cursor: pointer;
  &:focus { outline: none; border-color: #0369A1; }
`;

const TableWrap = styled.div`
  background: #fff;
  border: 1.5px solid #E2EAF4;
  border-radius: 14px;
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
`;

const Th = styled.th<{ $highlight?: boolean }>`
  padding: 10px 12px;
  text-align: right;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${(p) => p.$highlight ? '#0369A1' : '#94A3B8'};
  background: ${(p) => p.$highlight ? '#EFF6FF' : '#F8FAFC'};
  border-bottom: 1px solid #E2EAF4;
  white-space: nowrap;
  &:first-child, &:nth-child(2) { text-align: left; }
`;

const Td = styled.td<{ $highlight?: boolean }>`
  padding: 9px 12px;
  color: #374151;
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  text-align: right;
  background: ${(p) => p.$highlight ? '#F0F9FF' : 'transparent'};
  font-weight: ${(p) => p.$highlight ? 700 : 400};
  color: ${(p) => p.$highlight ? '#0369A1' : '#374151'};
  &:first-child, &:nth-child(2) { text-align: left; }
`;

const Tr = styled.tr`
  &:hover td { background: #F8FAFC; }
`;

const EmptyState = styled.div`
  padding: 56px 20px;
  text-align: center;
  color: #94A3B8;
  font-size: 14px;
  line-height: 1.6;
`;

const EmptyIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

// ─── Types ───────────────────────────────────────────────────────────────────

interface Session {
  id: string;
  session_date: string;
  session_type: string;
  source_filename: string;
  shot_count: number;
  created_at: string;
  patient_account_id?: string | null;
  patient_name?: string | null;
}

interface Shot {
  id: string;
  session_id: string;
  session_date: string;
  shot_number: number;
  club: string;
  ball_speed_mph: number | null;
  launch_deg: number | null;
  back_spin_rpm: number | null;
  side_spin_rpm: number | null;
  side_deg: number | null;
  flight_offline_yd: number | null;
  carry_yd: number | null;
  roll_yd: number | null;
  total_yd: number | null;
  flight_sec: number | null;
  descent_deg: number | null;
  height_yd: number | null;
  club_speed_mph: number | null;
  smash_factor: number | null;
  path_deg: number | null;
  ftp_deg: number | null;
  ftt_deg: number | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const f = (v: number | null, d = 1) => (v == null ? '—' : v.toFixed(d));

const avgOf = (shots: Shot[], field: keyof Shot): number | null => {
  const vals = shots.map((s) => s[field] as number | null).filter((v): v is number => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
};

const maxOf = (shots: Shot[], field: keyof Shot): number | null => {
  const vals = shots.map((s) => s[field] as number | null).filter((v): v is number => v != null);
  return vals.length ? Math.max(...vals) : null;
};

const fmtDate = (d: string) => {
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const CLUB_COLORS: Record<string, string> = {
  DRIVER: '#0369A1',
  '3 WOOD': '#0891B2',
  '5 WOOD': '#0E7490',
  HYBRID: '#0D9488',
  '3 IRON': '#059669',
  '4 IRON': '#16a34a',
  '5 IRON': '#15803D',
  '6 IRON': '#4F7942',
  '7 IRON': '#65A30D',
  '8 IRON': '#CA8A04',
  '9 IRON': '#B45309',
  'PW': '#D97706',
  'GW': '#EA580C',
  'SW': '#DC2626',
  'LW': '#BE185D',
};

const clubColor = (club: string) =>
  CLUB_COLORS[club.toUpperCase()] || '#6366F1';

// ─── Component ───────────────────────────────────────────────────────────────

const GolfMarker: React.FC = () => {
  const isPatientSession = useSelector(UserSelectors().isPatientSession);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [shots, setShots] = useState<Shot[]>([]);
  const [activeSession, setActiveSession] = useState<string>('all');
  const [activeClub, setActiveClub] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    try {
      const [sRes, shRes] = await Promise.all([
        (api as any).getSkyTrakSessions(),
        (api as any).getSkyTrakShots(),
      ]);
      if (sRes?.success) setSessions(sRes.sessions ?? []);
      if (shRes?.success) setShots(shRes.shots ?? []);
    } catch {
      // no data yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this SkyTrak session? This cannot be undone.')) return;
    try {
      await (api as any).deleteSkyTrakSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      setActiveSession(prev => prev === sessionId ? 'all' : prev);
      setShots(prev => prev.filter(s => s.session_id !== sessionId));
    } catch {
      alert('Failed to delete session. Please try again.');
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => f.name.endsWith('.csv'));
    if (!arr.length) {
      setUploadMsg({ text: 'Please select .csv files exported from SkyTrak.', ok: false });
      return;
    }
    setUploading(true);
    setUploadMsg(null);
    try {
      const res = await (api as any).uploadSkyTrakCSV(arr);
      setUploadMsg({
        text: `Imported ${res.shots_saved} shot${res.shots_saved !== 1 ? 's' : ''} across ${res.sessions_created} session${res.sessions_created !== 1 ? 's' : ''}.`,
        ok: true,
      });
      await loadData();
    } catch (e: any) {
      setUploadMsg({ text: e?.data?.error || e?.message || 'Upload failed.', ok: false });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Derive displayed shots from filters
  const displayedShots = shots.filter((s) => {
    if (activeSession !== 'all' && s.session_id !== activeSession) return false;
    if (activeClub !== 'all' && s.club.toUpperCase() !== activeClub) return false;
    return true;
  });

  // Shots for hero stats: all shots (no filter)
  const allShots = shots;
  const totalSessions = sessions.length;
  const totalShots = allShots.length;
  const avgCarry = avgOf(allShots, 'carry_yd');
  const avgBallSpeed = avgOf(allShots, 'ball_speed_mph');
  const avgSmash = avgOf(allShots, 'smash_factor');
  const bestCarry = maxOf(allShots, 'carry_yd');

  // Club breakdown
  const clubs = Array.from(new Set(allShots.map((s) => s.club).filter(Boolean)));
  const clubStats = clubs.map((club) => {
    const cs = allShots.filter((s) => s.club === club);
    return {
      club,
      count: cs.length,
      ballSpeed: avgOf(cs, 'ball_speed_mph'),
      carry: avgOf(cs, 'carry_yd'),
      total: avgOf(cs, 'total_yd'),
      smash: avgOf(cs, 'smash_factor'),
    };
  }).sort((a, b) => (b.carry ?? 0) - (a.carry ?? 0));

  const allClubs = Array.from(new Set(shots.map((s) => s.club).filter(Boolean)));

  return (
    <Page>
      {/* ── Top bar ─────────────────────────────────────────── */}
      <TopBar>
        <TitleBlock>
          <Title>Golf Performance Marker</Title>
          <Subtitle>
            {totalSessions > 0
              ? `${totalSessions} session${totalSessions !== 1 ? 's' : ''} · ${totalShots} total shots`
              : 'Upload a SkyTrak CSV to get started'}
          </Subtitle>
        </TitleBlock>

        {isPatientSession && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            <UploadZone
              $dragging={dragging}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
            >
              <UploadIcon>⛳</UploadIcon>
              <UploadText>
                <UploadLabel>{uploading ? 'Importing…' : 'Import SkyTrak CSV'}</UploadLabel>
                <UploadHint>Drop file or click to browse</UploadHint>
                {uploadMsg && <UploadStatus $ok={uploadMsg.ok}>{uploadMsg.text}</UploadStatus>}
              </UploadText>
            </UploadZone>
          </>
        )}
      </TopBar>

      {/* ── Hero stats ──────────────────────────────────────── */}
      <HeroRow>
        <HeroCard $gradient="linear-gradient(135deg, #0F2042 0%, #1E4FA8 100%)">
          <HeroLabel>Sessions</HeroLabel>
          <HeroValue>{totalSessions || '—'}</HeroValue>
        </HeroCard>
        <HeroCard $gradient="linear-gradient(135deg, #065F46 0%, #059669 100%)">
          <HeroLabel>Avg Carry</HeroLabel>
          <HeroValue>
            {f(avgCarry, 0)}
            {avgCarry != null && <HeroUnit>yd</HeroUnit>}
          </HeroValue>
        </HeroCard>
        <HeroCard $gradient="linear-gradient(135deg, #0C4A6E 0%, #0EA5E9 100%)">
          <HeroLabel>Avg Ball Speed</HeroLabel>
          <HeroValue>
            {f(avgBallSpeed, 0)}
            {avgBallSpeed != null && <HeroUnit>mph</HeroUnit>}
          </HeroValue>
        </HeroCard>
        <HeroCard $gradient="linear-gradient(135deg, #4C1D95 0%, #7C3AED 100%)">
          <HeroLabel>Avg Smash</HeroLabel>
          <HeroValue>{f(avgSmash, 2)}</HeroValue>
        </HeroCard>
        <HeroCard $gradient="linear-gradient(135deg, #92400E 0%, #F59E0B 100%)">
          <HeroLabel>Best Carry</HeroLabel>
          <HeroValue>
            {f(bestCarry, 0)}
            {bestCarry != null && <HeroUnit>yd</HeroUnit>}
          </HeroValue>
        </HeroCard>
      </HeroRow>

      {/* ── Sessions + Club Breakdown ────────────────────────── */}
      {(sessions.length > 0 || loading) && (
        <ContentRow>
          {/* Sessions */}
          <Panel>
            <PanelHead>
              Sessions
              {sessions.length > 0 && <PanelCount>{sessions.length}</PanelCount>}
            </PanelHead>
            <SessionList>
              {loading ? (
                <SessionItem $active={false} style={{ cursor: 'default' }}>
                  <SessionDate style={{ color: '#94A3B8' }}>Loading…</SessionDate>
                </SessionItem>
              ) : (
                <>
                  <SessionItem
                    $active={activeSession === 'all'}
                    onClick={() => { setActiveSession('all'); setActiveClub('all'); }}
                  >
                    <SessionDate>All Sessions</SessionDate>
                    <SessionMeta>
                      <span>{totalShots} shots</span>
                    </SessionMeta>
                  </SessionItem>
                  {sessions.map((s) => (
                    <SessionItem
                      key={s.id}
                      $active={activeSession === s.id}
                      onClick={() => { setActiveSession(s.id); setActiveClub('all'); }}
                    >
                      <SessionRow>
                        <div>
                          <SessionDate>{fmtDate(s.session_date)}</SessionDate>
                          <SessionMeta>
                            <Badge $color="#0369A1">{s.session_type}</Badge>
                            <span>{s.shot_count} shots</span>
                            {!isPatientSession && (s.patient_name || s.patient_account_id) && (
                              <span style={{ color: '#0369A1', fontWeight: 600 }}>
                                {s.patient_name || s.patient_account_id!.slice(0, 8)}
                              </span>
                            )}
                          </SessionMeta>
                        </div>
                        <SessionDeleteBtn
                          title="Delete session"
                          onClick={(e) => handleDeleteSession(e, s.id)}
                        >
                          ×
                        </SessionDeleteBtn>
                      </SessionRow>
                    </SessionItem>
                  ))}
                </>
              )}
            </SessionList>
          </Panel>

          {/* Club breakdown */}
          <Panel>
            <PanelHead>Club Breakdown</PanelHead>
            <TableScroll>
              {clubStats.length === 0 ? (
                <EmptyState style={{ padding: '32px 20px' }}>No shot data yet.</EmptyState>
              ) : (
                <ClubTable>
                  <thead>
                    <tr>
                      <ClubTh>Club</ClubTh>
                      <ClubTh>Shots</ClubTh>
                      <ClubTh>Ball Speed</ClubTh>
                      <ClubTh>Avg Carry</ClubTh>
                      <ClubTh>Avg Total</ClubTh>
                      <ClubTh>Smash</ClubTh>
                    </tr>
                  </thead>
                  <tbody>
                    {clubStats.map((c) => (
                      <tr key={c.club}>
                        <ClubTd $bold>
                          <ClubDot $color={clubColor(c.club)} />
                          {c.club}
                        </ClubTd>
                        <ClubTd>{c.count}</ClubTd>
                        <ClubTd>{f(c.ballSpeed)} mph</ClubTd>
                        <ClubTd $bold $color="#059669">{f(c.carry, 0)} yd</ClubTd>
                        <ClubTd>{f(c.total, 0)} yd</ClubTd>
                        <ClubTd>{f(c.smash, 2)}</ClubTd>
                      </tr>
                    ))}
                  </tbody>
                </ClubTable>
              )}
            </TableScroll>
          </Panel>
        </ContentRow>
      )}

      {/* ── Shot Log ────────────────────────────────────────── */}
      <SectionHead>
        <SectionTitle>
          Shot Log
          {displayedShots.length > 0 && (
            <span style={{ fontWeight: 400, color: '#64748B', marginLeft: 8, fontSize: 13 }}>
              {displayedShots.length} shots
            </span>
          )}
        </SectionTitle>
        {shots.length > 0 && (
          <FilterRow>
            <Select value={activeSession} onChange={(e) => setActiveSession(e.target.value)}>
              <option value="all">All Sessions</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>{fmtDate(s.session_date)}</option>
              ))}
            </Select>
            <Select value={activeClub} onChange={(e) => setActiveClub(e.target.value)}>
              <option value="all">All Clubs</option>
              {allClubs.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </FilterRow>
        )}
      </SectionHead>

      <TableWrap>
        <TableScroll>
          {loading ? (
            <EmptyState>Loading…</EmptyState>
          ) : shots.length === 0 ? (
            <EmptyState>
              <EmptyIcon>⛳</EmptyIcon>
              No shot data yet.
              <br />
              Export a session from SkyTrak (Activity → Export) and drop the CSV above.
            </EmptyState>
          ) : displayedShots.length === 0 ? (
            <EmptyState>No shots match the current filter.</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th style={{ textAlign: 'left' }}>#</Th>
                  <Th style={{ textAlign: 'left' }}>Club</Th>
                  <Th $highlight>Club Spd</Th>
                  <Th $highlight>Smash</Th>
                  <Th>Ball Spd</Th>
                  <Th>Launch</Th>
                  <Th>Back Spin</Th>
                  <Th>Side Spin</Th>
                  <Th>Side</Th>
                  <Th>Offline</Th>
                  <Th>Carry</Th>
                  <Th>Roll</Th>
                  <Th>Total</Th>
                  <Th>Flight</Th>
                  <Th>Descent</Th>
                  <Th>Height</Th>
                  <Th>Path</Th>
                  <Th>FTP</Th>
                  <Th>FTT</Th>
                </tr>
              </thead>
              <tbody>
                {displayedShots.map((s) => (
                  <Tr key={s.id}>
                    <Td style={{ textAlign: 'left', fontWeight: 600, color: '#0F2042' }}>{s.shot_number}</Td>
                    <Td style={{ textAlign: 'left' }}>
                      <ClubDot $color={clubColor(s.club)} />
                      {s.club || '—'}
                    </Td>
                    <Td $highlight>{f(s.club_speed_mph)} mph</Td>
                    <Td $highlight>{f(s.smash_factor, 2)}</Td>
                    <Td style={{ fontWeight: 600 }}>{f(s.ball_speed_mph)} mph</Td>
                    <Td>{f(s.launch_deg)}°</Td>
                    <Td>{f(s.back_spin_rpm, 0)}</Td>
                    <Td>{f(s.side_spin_rpm, 0)}</Td>
                    <Td>{f(s.side_deg)}°</Td>
                    <Td style={{ color: s.flight_offline_yd != null && Math.abs(s.flight_offline_yd) > 20 ? '#dc2626' : undefined }}>
                      {f(s.flight_offline_yd)} yd
                    </Td>
                    <Td style={{ color: '#059669', fontWeight: 600 }}>{f(s.carry_yd, 0)} yd</Td>
                    <Td>{f(s.roll_yd, 0)} yd</Td>
                    <Td>{f(s.total_yd, 0)} yd</Td>
                    <Td>{f(s.flight_sec, 1)}s</Td>
                    <Td>{f(s.descent_deg)}°</Td>
                    <Td>{f(s.height_yd, 0)} yd</Td>
                    <Td>{f(s.path_deg)}°</Td>
                    <Td>{f(s.ftp_deg)}°</Td>
                    <Td>{f(s.ftt_deg)}°</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableScroll>
      </TableWrap>
    </Page>
  );
};

export default GolfMarker;
